"use client";

import styles from "@/styles/category/index.module.scss";
import Image from "next/image";
import { useState } from "react";
import { Popup } from "react-vant";
import { ArrowUp, ArrowDown } from "@react-vant/icons";

interface FilterOption {
  type: string;
  // value: 用于排序等简单值；item: 用于复杂对象（如分类项）
  value?: string;
  item?: any;
}

interface FilterBoxProps {
  onSortChange: (option: FilterOption) => void;
  sidebarFilter?: any[];
  showFilter?: boolean; // 控制 filter 图标的显示，默认为 true
}

export default function FilterBox({
  onSortChange,
  sidebarFilter,
  showFilter = true, // 默认显示 filter
}: FilterBoxProps) {
  // 跟踪当前选中的筛选项
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | null>(
    null
  );
  const [visible, setVisible] = useState(false);
  // 跟踪每个筛选项的展开/收起状态
  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});
  // 跟踪每个筛选项的选中状态
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: any }>(
    {}
  );
  // 跟踪当前排序显示文案
  const [sortByText, setSortByText] = useState<string>("Popularity");

  // 切换筛选项的展开/收起状态
  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 关闭弹窗的处理函数
  const handleClosePopup = () => {
    setVisible(false);
    // 重置所有展开状态
    setExpandedItems({});
  };

  // 检查某个筛选项是否被选中
  const isItemSelected = (filterType: string, item: any) => {
    const key = `${filterType}:${
      item.value || item.name || item.title || JSON.stringify(item)
    }`;
    return selectedItems[key] !== undefined;
  };

  // 根据筛选项类型渲染不同的内容
  const renderFilterContent = (filterItem: any) => {
    const hasItems = filterItem.items && filterItem.items.length > 0;
    const isExpanded = expandedItems[filterItem.key];

    switch (filterItem.key) {
      case "category":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.categoryItemsList}>
                {filterItem.items.map((item: any, index: number) => {
                  const itemKey = `${filterItem.key}:${
                    item.value || item.name || item.title || index
                  }`;
                  const hasChildren =
                    Array.isArray(item.subCategories) &&
                    item.subCategories.length > 0;

                  return (
                    <div key={itemKey} className={styles.categoryItemWrapper}>
                      <div
                        className={`${styles.categoryItem} ${
                          isItemSelected("category", item)
                            ? styles.selected
                            : ""
                        }`}
                        onClick={() =>
                          handleFilterOptionClick("category", item)
                        }
                      >
                        <span>{item.name || item.title}</span>
                      </div>
                      {hasChildren && (
                        <div className={styles.categoryItemsList}>
                          {item.subCategories.map(
                            (child: any, childIndex: number) => (
                              <div
                                key={`${itemKey}:sub:${
                                  child.value ||
                                  child.name ||
                                  child.title ||
                                  childIndex
                                }`}
                                className={`${styles.categoryItem} ${
                                  isItemSelected("category", child)
                                    ? styles.selected
                                    : ""
                                }`}
                                onClick={() =>
                                  handleFilterOptionClick("category", child)
                                }
                              >
                                {child.name || child.title}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case "price-range":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.priceRangeItemsList}>
                {filterItem.items.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.priceRangeItem} ${
                      isItemSelected(filterItem.key, item)
                        ? styles.selected
                        : ""
                    }`}
                    onClick={() =>
                      handleFilterOptionClick(filterItem.key, item)
                    }
                  >
                    {item.name || item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "hot-brand":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.brandItemsList}>
                {filterItem.items.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.brandItem} ${
                      isItemSelected(filterItem.key, item)
                        ? styles.selected
                        : ""
                    }`}
                    onClick={() =>
                      handleFilterOptionClick(filterItem.key, item)
                    }
                  >
                    {item.name || item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "popularity":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
            </div>
          </div>
        );

      default:
        return <div>Unknown filter type</div>;
    }
  };

  // 处理筛选选项点击
  const handleFilterOptionClick = (filterType: string, option: any) => {
    const key = `${filterType}:${
      option.value || option.name || option.title || JSON.stringify(option)
    }`;
    const isSelected = selectedItems[key] !== undefined;

    const filterOption: FilterOption = {
      type: filterType,
      item: option,
    };

    // 清空顶部排序按钮的选择（handleSortClick 的选择）
    if (
      selectedFilter &&
      (selectedFilter.type === "sort" || selectedFilter.type === "filter")
    ) {
      setSelectedFilter(null);
    }

    // 更新排序文案为 Popularity
    setSortByText("Popularity");

    // 更新选中状态 - 全局单选模式
    setSelectedItems((prev) => {
      const newState = { ...prev };

      if (isSelected) {
        // 如果已选中，则取消选中
        delete newState[key];
      } else {
        // 如果未选中，则选中
        // 先清除所有其他选中项（全局单选）
        Object.keys(newState).forEach((existingKey) => {
          delete newState[existingKey];
        });
        // 然后选中当前项
        newState[key] = option;
      }
      return newState;
    });

    // 调用父组件的回调函数
    onSortChange(filterOption);

    // 更新选中状态
    setSelectedFilter(filterOption);

    // 关闭弹窗
    handleClosePopup();
  };

  const handleSortClick = (option: FilterOption) => {
    // 清空 Popup 中的筛选选择（handleFilterOptionClick 的选择）
    setSelectedItems({});

    // 价格排序的特殊处理：三态切换
    if (option.type === "sort" && option.value === "price") {
      let newPriceSort = null;

      if (
        !selectedFilter ||
        (selectedFilter.value !== "price-low-to-high" &&
          selectedFilter.value !== "price-high-to-low")
      ) {
        // 当前未选中价格排序，切换到低到高
        newPriceSort = { type: "sort", value: "price-low-to-high" };
      } else if (selectedFilter.value === "price-low-to-high") {
        // 当前是低到高，切换到高到低
        newPriceSort = { type: "sort", value: "price-high-to-low" };
      } else {
        // 当前是高到低，切换到低到高
        newPriceSort = { type: "sort", value: "price-low-to-high" };
      }

      setSelectedFilter(newPriceSort);
      setSortByText("Price");
      onSortChange(newPriceSort || { type: "sort", value: "price" });
      return;
    }

    // 更新排序文案
    if (option.value === "bestMatch") {
      setSortByText("Best Match");
    } else if (option.value === "new-arrivals") {
      setSortByText("New Arrivals");
    } else if (option.value === "freeshipping") {
      setSortByText("Free Shipping");
    }

    // 更新选中状态并触发回调
    setSelectedFilter(option);
    onSortChange(option);
  };

  return (
    <div className={styles["content-filter-box"]}>
      <div className={styles["content-filter"]}>
        <div className={styles["sort-title"]}>Sort by</div>
        <div className={styles["sort-by"]}>{sortByText}</div>
      </div>

      <div className={styles["sort-box"]}>
        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-all${
            selectedFilter?.type === "sort" &&
            selectedFilter?.value === "bestMatch"
              ? "-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() => handleSortClick({ type: "sort", value: "bestMatch" })}
        />

        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-price${
            selectedFilter?.value === "price-low-to-high"
              ? "-high-red"
              : selectedFilter?.value === "price-high-to-low"
              ? "-low-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() => handleSortClick({ type: "sort", value: "price" })}
        />

        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-new${
            selectedFilter?.type === "sort" &&
            selectedFilter?.value === "new-arrivals"
              ? "-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() =>
            handleSortClick({ type: "sort", value: "new-arrivals" })
          }
        />

        <Image
          className={styles["sort-item"]}
          src={`${
            process.env.IMAGE_DOMAIN
          }/svg/category-page/icon-free-shipping${
            selectedFilter?.type === "filter" &&
            selectedFilter?.value === "freeshipping"
              ? "-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() =>
            handleSortClick({ type: "filter", value: "freeshipping" })
          }
        />
      </div>

      <Image
        src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-filter.svg`}
        alt="Filter"
        className={styles["filter-icon"]}
        width={30}
        height={30}
        onClick={() => setVisible(true)}
        style={{ visibility: showFilter ? "visible" : "hidden" }}
      />

      <Popup
        visible={visible}
        closeable
        title="Filter"
        style={{ height: "80%" }}
        position="bottom"
        round
        onClose={handleClosePopup}
      >
        <div className={styles.filterContent}>
          {sidebarFilter?.map((filterItem, index) => (
            <div key={index} className={styles.filterSection}>
              {renderFilterContent(filterItem)}
            </div>
          ))}
        </div>
      </Popup>
    </div>
  );
}
